import cx from 'clsx';
import React from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { IconType } from 'react-icons';

type options_getter = () => Map<string, string | number> | Promise<Map<string, string | number>>;
type options_list = (string | number)[] | Record<string, string | number>;

type Props = {
  children?: React.ReactNode;
  rootClassName?: string;
  labelClassName?: string;
  className?: string;
  name: string;
  label?: string | React.ReactNode;
  placeholder?: string;
  description?: React.ReactNode;
  readOnly?: boolean;
  RightIcon?: IconType;
  registerOptions?: RegisterOptions;
  autoSubmitForm?: string;
  options: options_getter | options_list;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

export const Select: React.FC<Props> = ({
  rootClassName,
  labelClassName,
  className,
  name,
  label,
  placeholder,
  description,
  readOnly,
  options: opts,
  registerOptions,
  autoSubmitForm,
  children,
  ...otherProps
}) => {
  const { register, formState } = useFormContext();

  const errors = formState.errors[name];
  const errorContent = errors?.types ? Object.values(errors.types).map((text, index) => <small key={index}>{text}</small>) : null;

  const options = React.useMemo(() => {
    const result: Record<string, string | number> = {};
    if (Array.isArray(opts)) for (const value of opts) result[value] = value;
    else Object.assign(result, opts);
    return result;
  }, [opts]);

  const onChange = React.useCallback(() => {
    if (!autoSubmitForm) return;
    const form = document.querySelector(autoSubmitForm) as HTMLFormElement;
    if (!form) return console.warn(`Select: form with selector ${autoSubmitForm} could not be found.`);
    form.requestSubmit();
  }, [autoSubmitForm]);

  return (
    <div className={cx('relative flex flex-col', rootClassName)} data-testid="control-input">
      {label && (
        <label className={cx('control-label', labelClassName)} htmlFor={`input-${name}`}>
          {label}
        </label>
      )}
      <select
        id={`select-${name}`}
        className={cx(
          // hack: use invalid:* to style the placeholder
          'relative mt-1 h-[44px] w-full border border-grey-border px-3 py-2 invalid:text-slate-500 focus:border-blue disabled:cursor-not-allowed disabled:bg-gray-200',
          errorContent && 'border-red-dark',
          className,
          readOnly && 'pointer-events-none bg-none',
        )}
        data-testid="select"
        defaultValue=""
        required={true} /* hack: use the :invalid selector to style placeholder */
        {...register(name, { onChange, ...registerOptions })}
        {...otherProps}
      >
        {placeholder && (
          <option disabled={registerOptions?.required ? true : false} value="">
            {placeholder}
          </option>
        )}
        {Object.entries(options).map(([key, value]) => (
          <option key={key} value={key}>
            {value as string}
          </option>
        ))}
      </select>
      {description && <span>{description}</span>}
      {errorContent && (
        <div data-testid="control-errors" className="red flex flex-col gap-1">
          {errorContent}
        </div>
      )}
      {children}
    </div>
  );
};
